import cookieControl from '../../methods/cookieControl'

let inFlightController = null

const cloneDeep = (value) => value == null ? value : JSON.parse(JSON.stringify(value))

const convertSelectedFacetsToQueryString = function(facets){

    const prefix = function(string){
        return string.length ? '&' : ''
    }

    let queryString = ''

    for(let facet in facets){
        var keyString = facet
        var facetString = facets[facet].join(',')
        queryString += `${prefix(queryString)}${keyString}=${encodeURIComponent(facetString)}` 
    }

    return queryString

}

const convertSortOptionsToQueryString = function(searchConfig){
    const sortBy = searchConfig.sortBy
    const sortOrder = searchConfig.sortOrder
    let queryString = ''

    if(sortBy && sortOrder){
        queryString = `sortOrder=${sortOrder}&sortBy=${sortBy}&`
    }

    return queryString

}

const createSelectedFacetsObject = function(categories){
    const selectedFacets = {}

    categories.forEach(category => {
        const selectedFacetsArray = category.facets.reduce((total,current)=>{
            if(current.selected){
                total.push(current.key)
            }
            return total
        },[])

        if(selectedFacetsArray.length){
            selectedFacets[category.key] = selectedFacetsArray
        }
    })

    return selectedFacets
}

const formatCategoryViewData = function(response,typeName,groupName,otherFacetGroups){

    const facetsList = response.categories.find(category => category.key === typeName).facets

    const formattedCategory =  {
        key: response.categories.find(category => category.key === typeName).key,
        displayName: response.categories.find(category => category.key === typeName).displayName,
        documentCount: response.categories.find(category => category.key === typeName).hits,
        selected: facetsList.length ? facetsList[0].selected : '',
        value: facetsList.length ? facetsList[0].key : '',
        facetGroups: []
    }

    if(groupName && response.categories.find(category => category.key === groupName)){
        const group = response.categories.find(category => category.key === groupName)
        const products = {
            key: group.key,
            values: group.facets,
            displayName: group.displayName
        }
        formattedCategory.facetGroups.push(products)
    }

    if(otherFacetGroups){
        otherFacetGroups.forEach(groupName =>{
            const group = response.categories.find(category => category.key === groupName)

            formattedCategory.facetGroups.push({
                key: group.key,
                displayName: group.displayName,
                values: group.facets
            })
        })
    }

    return formattedCategory
}

export default {
    namespaced: true,
    state: {
        results : null,
        categories: [],
        selectedFacets: {},
        // hasError = nothing to show (initial load failed)
        // hasStaleWarning = a search failed but we still have previous results to display
        hasError: false,
        hasStaleWarning: false,
        // Snapshot of the last successful search state — used to roll back filter UI
        // when a follow-up search fails so the rendered results and the filter chips
        // stay in sync.
        committedSnapshot: null,
        searchConfig: {
            skip: 0,
            take: 12,
            puid: '00000000-0000-0000-0000-000000000000',
            language: "da"
        },
    },
    mutations:{
        setResults(state,results){
            state.results = results
        },
        setCategories(state,categories){
            state.categories = categories
        },
        clearCategories(state){
            state.categories = []
        },
        setSelectedFacets(state,selectedFacets){
            state.selectedFacets = selectedFacets
        },
        setSearchConfig(state,result){
            delete result.hits
            const newConfig = Object.assign({},state.searchConfig,result)
            state.searchConfig = newConfig
        },
        setPuid(state,puid){
            state.searchConfig.puid = puid
        },
        setLanguageCode(state,lang){
            state.searchConfig.language = lang
        },
        setHasError(state,value){
            state.hasError = !!value
        },
        setHasStaleWarning(state,value){
            state.hasStaleWarning = !!value
        },
        setCommittedSnapshot(state,snapshot){
            state.committedSnapshot = snapshot
        }
    },
    actions:{
        performSearch(context){
            const url = process.env.FILTER_SEARCH
            let config = {}

            const searchConfig = Object.assign({},context.state.searchConfig,context.state.selectedFacets)

            if(inFlightController){
                inFlightController.abort()
            }
            inFlightController = new AbortController()
            const signal = inFlightController.signal

            config = {
                body: JSON.stringify(searchConfig),
                method: 'POST',
                mode: 'cors',
                headers : {
                    'Content-Type': 'application/json'
                },
                signal
            }

            context.commit('setHasError', false)
            context.commit('setHasStaleWarning', false)

            return fetch(url,config)
            .then(res => {
                if(res.ok){
                    return res.json()
                } else {
                    throw new Error('Search error')
                }
            })
            .then(json => {
                if(json && json.hasError){
                    context.dispatch('handleSearchFailure')
                    return
                }
                return context.dispatch('processSearchResponse',json)
            })
            .catch(err => {
                if(err && err.name === 'AbortError'){
                    return
                }
                console.error(err)
                context.dispatch('handleSearchFailure')
            })
        },
        handleSearchFailure(context){
            const snapshot = context.state.committedSnapshot
            if(snapshot){
                // Roll filter UI back to the state that matches the still-rendered
                // results, and show a small warning above them.
                context.commit('setSelectedFacets', cloneDeep(snapshot.selectedFacets))
                context.commit('setSearchConfig', cloneDeep(snapshot.searchConfig))
                context.commit('setCategories', cloneDeep(snapshot.categories))
                context.commit('setHasStaleWarning', true)
            } else {
                // No previous successful search to fall back on — render the
                // empty-state banner instead.
                context.commit('setResults', [])
                context.commit('clearCategories')
                context.commit('setHasError', true)
            }
        },
        processSearchResponse(context,response){
            context.commit('clearCategories')
            context.commit('setResults',response.result.hits.map(hit => hit.content))
            context.commit('setSearchConfig',response.result)
            context.commit('setCategories',[
                formatCategoryViewData(response,'ProductTypeHardware','ProductGroupHardware'),
                formatCategoryViewData(response,'ProductTypeSoftware','ProductGroupSoftware'),
                formatCategoryViewData(response,'ProductTypeCourse','ProductGroupCourse',['CourseLevel','Location']),
                formatCategoryViewData(response,'Industry','Industry'),
                formatCategoryViewData(response,'Manufacturer','Manufacturer'),
            ])
            context.commit('setSelectedFacets',createSelectedFacetsObject(response.categories))
            context.commit('setCommittedSnapshot',{
                selectedFacets: cloneDeep(context.state.selectedFacets),
                searchConfig: cloneDeep(context.state.searchConfig),
                categories: cloneDeep(context.state.categories)
            })
            context.dispatch('composeBrowserUrl')
        },
        composeBrowserUrl(context){
            const facets = context.state.selectedFacets
            const searchConfig = context.state.searchConfig
            const facetString = convertSelectedFacetsToQueryString(facets)
            const pagerString = searchConfig.skip ? `skip=${searchConfig.skip}&` : ''
            const sortString = (pagerString ? '&' : '')+convertSortOptionsToQueryString(searchConfig)

            const queryString = '?'+pagerString+sortString+facetString
            const browserState = JSON.parse(JSON.stringify(facets))

            history.replaceState(browserState,'search-url',queryString)

            context.dispatch('saveFiltersInCookie',queryString)
        },
        saveFiltersInCookie(context,queryString){
            if (typeof queryString !== 'string' || queryString.length === 0) {
                return
            }
            const expireAt = 86400 // 24 hours
            cookieControl.setItem('shopfilter',decodeURIComponent(queryString),expireAt, '/')
        }

    }
}
